import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

/**
 * Links a parsed statement to a bank profile. Account identifiers are taken from
 * the parsed file on the server; the client only chooses the target.
 */
export default function LinkStatementForm({ statement, meta, onLinked }) {
  const queryClient = useQueryClient();
  const draft = statement.report?.draft_profile || {};
  const currency = statement.currency_code || draft.currency_code;

  const sameCurrencyProfiles = useMemo(
    () => (meta?.bank_profiles || []).filter((p) => p.is_active && p.currency_code === currency),
    [meta, currency]
  );

  const [mode, setMode] = useState(
    sameCurrencyProfiles.length ? "existing" : (meta?.companies || []).length ? "new_profile" : "new_company"
  );
  const [profileId, setProfileId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [label, setLabel] = useState(draft.bank_name ? `${draft.bank_name} ${currency || ""}`.trim() : "");
  const [company, setCompany] = useState({
    name: draft.client_name || "",
    tax_id: "",
    country: "",
    base_currency: currency || "",
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (mode === "existing") return GeneralLedgerService.linkStatement(statement.id, { bank_profile_id: Number(profileId) });
      if (mode === "new_profile") return GeneralLedgerService.linkStatement(statement.id, { company_id: Number(companyId), label: label || null });
      return GeneralLedgerService.linkStatement(statement.id, {
        label: label || null,
        company: {
          name: company.name.trim(),
          tax_id: company.tax_id.trim() || null,
          country: company.country.trim() || null,
          base_currency: company.base_currency.trim() || null,
        },
      });
    },
    onSuccess: (res) => {
      toast.success("Statement linked to the bank profile");
      ["glMeta", "glStatements", "glEntries", "glBankProfiles", "glCompanies"].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
      onLinked?.(res.data);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not link the statement.")),
  });

  const canSubmit =
    (mode === "existing" && profileId) ||
    (mode === "new_profile" && companyId) ||
    (mode === "new_company" && company.name.trim().length >= 2);

  return (
    <div className="gl-link-form">
      <p className="mb-2">
        No bank profile matches this account yet
        {draft.iban_masked ? ` (IBAN ${draft.iban_masked})` : ""}. Future statements for the same account will be linked
        automatically once a profile exists.
      </p>
      <div className="d-flex gap-3 flex-wrap mb-3">
        {[
          ["existing", "Existing profile", sameCurrencyProfiles.length === 0],
          ["new_profile", "New profile in an existing company", (meta?.companies || []).length === 0],
          ["new_company", "New company and profile", false],
        ].map(([value, text, disabled]) => (
          <FormGroup check key={value}>
            <Input
              type="radio"
              id={`gl-link-${value}`}
              name="gl-link-mode"
              checked={mode === value}
              disabled={disabled}
              onChange={() => setMode(value)}
            />
            <Label check for={`gl-link-${value}`}>{text}</Label>
          </FormGroup>
        ))}
      </div>

      {mode === "existing" && (
        <FormGroup>
          <Label>Bank profile ({currency})</Label>
          <Input type="select" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
            <option value="">Select…</option>
            {sameCurrencyProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label || p.bank_name} ****{p.account_last4}
              </option>
            ))}
          </Input>
          <small className="gl-muted">The server verifies that the account number in the file matches this profile.</small>
        </FormGroup>
      )}

      {mode !== "existing" && (
        <FormGroup>
          <Label>Profile label</Label>
          <Input value={label} maxLength={191} onChange={(e) => setLabel(e.target.value)} />
        </FormGroup>
      )}

      {mode === "new_profile" && (
        <FormGroup>
          <Label>Company</Label>
          <Input type="select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">Select…</option>
            {(meta?.companies || []).filter((c) => c.is_active).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Input>
        </FormGroup>
      )}

      {mode === "new_company" && (
        <Row className="g-2">
          <Col md="6">
            <Label>Company name</Label>
            <Input value={company.name} maxLength={191} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
          </Col>
          <Col md="6">
            <Label>Tax ID</Label>
            <Input value={company.tax_id} maxLength={64} onChange={(e) => setCompany({ ...company, tax_id: e.target.value })} />
          </Col>
          <Col md="6">
            <Label>Country (ISO-2)</Label>
            <Input value={company.country} maxLength={2} placeholder="TN" onChange={(e) => setCompany({ ...company, country: e.target.value.toUpperCase() })} />
          </Col>
          <Col md="6">
            <Label>Base currency</Label>
            <Input value={company.base_currency} maxLength={3} onChange={(e) => setCompany({ ...company, base_currency: e.target.value.toUpperCase() })} />
          </Col>
        </Row>
      )}

      <div className="text-end mt-3">
        <Button color="primary" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Linking…" : "Link statement"}
        </Button>
      </div>
    </div>
  );
}
